import * as React from 'react';
import { Slider as KendoSlider, SliderLabel } from '@progress/kendo-react-inputs';
import styled, { css } from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { FieldProps, generateValidations } from '../../core/form';
import { phoneOnlyMaxWidth, singleColumnMaxWidth } from '../../appComponents/cssConstants';
import { DropDownListOption } from '../../models/DropDownListOption';

const SliderQuestion = (props: Props) => {
	const {
		name,
		data,
		sliderTitle,
		showSliderValue = false,
		fullWidth = true,
		showSelectedValue = true,
		customSelectedValue,
		className = ''
	} = props;
	const { setValue } = useFormContext();
	const { field } = useController({ name, rules: generateValidations(props) });
	const { value } = field;

	return (
		<Container className={className} $fullWidth={fullWidth}>
			<SliderQuestionLabel>{sliderTitle}</SliderQuestionLabel>
			<SliderWrapper>
				{showSelectedValue && (
					<div className="selected">
						{customSelectedValue ? customSelectedValue.find((c) => c.value === String(value))?.text : value}
					</div>
				)}
				<StyledSlider
					name={name}
					className="slider"
					value={generateSliderNumberValue(value, data)}
					min={data[0].value}
					max={data[data.length - 1].value}
					step={1} // This requires the 'data' values have same step.
					onChange={(e: { value: number }) => setValue(name, data[Math.round(e.value) - 1].label)}
				>
					{data.map((x: { label: string; value: number }) =>
						showSliderValue ? (
							<SliderLabel title={x.label} key={x.label} position={x.value} onClick={() => setValue(name, x.label)}>
								{x.label}
							</SliderLabel>
						) : (
							<SliderLabel title={x.label} key={x.label} position={x.value} onClick={() => setValue(name, x.label)} />
						)
					)}
				</StyledSlider>
				{!showSliderValue && (
					<div className="options">
						<span className="leftLabel">{data[0].label}</span>
						<span className="rightLabel">{data[data.length - 1].label}</span>
					</div>
				)}
			</SliderWrapper>
		</Container>
	);
};

interface Props extends FieldProps {
	sliderTitle: string;
	showSliderValue?: boolean;
	fullWidth?: boolean;
	showSelectedValue?: boolean;
	customSelectedValue?: DropDownListOption<string>[];
	className?: string;
}

export default SliderQuestion;

const generateSliderNumberValue = (fieldValue: string, options: { label: unknown }[]) => {
	if (fieldValue) {
		const index = options.findIndex((x: { label: unknown }) => x.label.toString() === fieldValue.toString());
		if (index === -1) throw new Error(`Invalid slider field value: ${fieldValue}`);
		return index + 1; // The values of slider start at 1 in questionData.ts.
	}
	return null;
};

const Container = styled.div<{ $fullWidth: boolean }>`
	display: block;
	margin: ${(props) => (props.$fullWidth ? '30px auto auto auto' : 'auto auto 42px auto')};
	text-align: center;
	max-width: 600px;

	@media (max-width: ${phoneOnlyMaxWidth}) {
		width: 100%;
	}

	@media (max-width: ${singleColumnMaxWidth}) {
		margin-top: 20px;
	}

	${(props) =>
		!props.$fullWidth &&
		css`
			display: inline-flex;
			flex-direction: column;
			position: relative;
		`}
`;

const SliderQuestionLabel = styled.label`
	display: block;
	margin: 8px;
	font-weight: bold;
	font-size: 18px;
	color: #606060;
`;

const SliderWrapper = styled.div`
	display: block;

	.selected {
		height: 52px;
		line-height: 52px;
		border-radius: 7px;
		margin-bottom: 7px;
		text-align: center;
		font-size: 18px;
		font-weight: bold;
		color: ${(props) => props.theme.brandingColourHeavy};
	}

	.options {
		margin-top: 8px;
		text-align: left;

		span {
			font-size: 14px;
			color: ${(props) => props.theme.brandingColourHeavy};
		}

		.rightLabel {
			float: right;
		}
	}
`;

const StyledSlider = styled(KendoSlider)`
	width: 100%;
	height: 38px;
	padding: 0 40px;
	background: ${(props) => props.theme.brandingColourLight};
	border-radius: 26px;

	.k-slider-track-wrap {
		.k-slider-items {
			.k-tick {
				height: 27px !important;
				border: 1px solid ${(props) => props.theme.brandingColourHeavy};
				background-image: none;
			}
		}

		.k-slider-track {
			background-color: transparent;

			.k-slider-selection {
				background-color: transparent;
			}

			.k-draghandle {
				width: 52px;
				height: 52px;
				border-color: ${(props) => props.theme.brandingColourHeavy};
				background: ${(props) => props.theme.brandingColourHeavy};
				box-shadow: none !important;

				&::before,
				&::after {
					content: '';
					display: inline-block !important;
					width: 0;
					height: 0;
					border-top: 6px solid transparent;
					border-bottom: 6px solid transparent;
					position: relative;
					top: 18px;
				}

				&::before {
					border-right: 11px solid #fff;
					margin-right: 3px;
				}

				&::after {
					border-left: 11px solid #fff;
				}
			}
		}
	}

	.k-label {
		bottom: -2.1em !important;
		font-size: 14px;
		color: ${(props) => props.theme.brandingColourHeavy};
	}
`;
